// График «Загрузки кухни»: столбик на час открытия заказа, внутри — цеха стопкой.
// Своя разметка (MUI Box + CSS grid), без библиотек графиков.
// Кодирование: цвет = цех, штриховка = статус. У каждого цеха в столбце сплошная часть (закрытые)
// и над ней заштрихованная тем же цветом (открытые); открытые без цеха — нейтральный блок сверху.
// Итог часа = закрытые + открытые (позиции, заказы, сумма) — над столбцом, в панели часа, в «Пике».
// - цеха по сумме позиций за период — больше ниже; легенда в том же порядке, «Без цеха» и «Открыто» —
//   после разделителя; чип выделяет цех (или все открытые части, или «Без цеха»), повторно — сброс;
// - выбранный час (по умолчанию — текущий при «Сегодня», иначе пиковый) расшифрован под графиком.
import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, DIVIDER_COLOR, MOTION, MUTED_TEXT_COLOR, REDUCED_MOTION, SECTION_ACCENTS, motionSx } from '../../../styles/tokens';
import { HATCH_SX, formatInt, formatMoney, itemsText, ordersText, placeColor, placeHatchSx } from '../kitchenFormat';

const ACCENT = SECTION_ACCENTS['/kitchen'].accent;
const NOW_COLOR = '#ff7043';
const NOW_TEXT = '#ff8a65';
const NOW_LINE = 'rgba(255,112,67,0.4)';
const OPEN_KEY = '__open';
const NO_COOK_KEY = '__nocook';

// Вертикальная раскладка колонки (px): чип «Пик», число позиций, столбик, подпись часа, «сейчас»
const PEAK_ROW = 20;
const COUNT_ROW = 20;
const ROW_GAP = 6;
const BARS_TOP = PEAK_ROW + COUNT_ROW + ROW_GAP * 2;
const BAR_HEIGHT = { xs: 180, md: 240 };
const GRID_LINES = [1 / 3, 2 / 3, 1]; // от основания, без подписей
const MIN_COLUMN = 28;
const FADE_WIDTH = 28;

const fadeSx = motionSx(['opacity'], MOTION.fast);
// Слой столбика: при смене периода или новых данных опроса высота меняется за MOTION.data.
// При первом показе графика перехода нет — элементы создаются сразу с нужной высотой
const layerSx = {
  transition: `opacity ${MOTION.fast}ms ${MOTION.easing}, height ${MOTION.data}ms ${MOTION.easing}`,
  [REDUCED_MOTION]: { transition: 'none' },
};

// Цвет на ~10% светлее: смешение с белым
function lighten(hex, amount = 0.1) {
  const value = hex.replace('#', '');
  const channels = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
  return `#${channels.map((c) => Math.round(c + (255 - c) * amount).toString(16).padStart(2, '0')).join('')}`;
}

const solidSx = (color) => ({ background: `linear-gradient(180deg, ${lighten(color)} 0%, ${color} 100%)` });

// Маска прокручиваемого блока: прозрачный край там, где за ним ещё есть столбцы
function fadeMaskSx(fade) {
  if (!fade.left && !fade.right) return {};
  const left = fade.left ? `transparent 0, #000 ${FADE_WIDTH}px` : '#000 0';
  const right = fade.right ? `#000 calc(100% - ${FADE_WIDTH}px), transparent 100%` : '#000 100%';
  const mask = `linear-gradient(to right, ${left}, ${right})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

// Горизонтальный скролл с затуханием того края, за которым ещё есть столбцы
function useScrollFade(ref) {
  const [fade, setFade] = useState({ left: false, right: false });
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const update = () => {
      const max = node.scrollWidth - node.clientWidth;
      setFade({ left: node.scrollLeft > 2, right: max - node.scrollLeft > 2 });
    };
    update();
    node.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      node.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref]);
  return fade;
}

function LegendChip({ label, count, swatchSx, pressed, dimmed, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={pressed}
      sx={{
        ...fadeSx,
        minHeight: 32,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        px: 1.25,
        gap: 1,
        borderRadius: '8px',
        fontSize: '13px',
        color: '#d4d4d8',
        opacity: dimmed ? 0.45 : 1,
        backgroundColor: pressed ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${pressed ? 'rgba(255,255,255,0.18)' : 'transparent'}`,
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
        '&.Mui-focusVisible': { outline: `2px solid ${ACCENT}`, outlineOffset: '2px' },
      }}
    >
      <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, ...swatchSx }} />
      {label}
      <Box component="span" sx={{ color: MUTED_TEXT_COLOR, fontVariantNumeric: 'tabular-nums' }}>
        {formatInt(count)}
      </Box>
    </ButtonBase>
  );
}

// Строки панели часа: цех — «35 · 8 открыто», полоска — сплошная часть + заштрихованная
function HourDetails({ hour, rows }) {
  if (!hour) return null;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Box sx={{ borderTop: `1px solid ${DIVIDER_COLOR}`, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }} aria-live="polite">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: '#fff' }}>{hour.label}</Typography>
        <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>
          {itemsText(hour.items)}
          {hour.openItems > 0 ? ` · из них ${formatInt(hour.openItems)} в открытых` : ''} · {ordersText(hour.orders)} ·{' '}
          {formatMoney(hour.sum)}
        </Typography>
      </Box>
      {rows.length > 0 && (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 1.25, md: '10px 32px' } }}>
          {rows.map((row) => (
            <Box component="li" key={row.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, ...row.dotSx }} />
                <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '14px', color: '#d4d4d8' }}>
                  {row.label}
                  {row.note && (
                    <Box component="span" sx={{ color: MUTED_TEXT_COLOR }}>
                      {' '}· {row.note}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                  {formatInt(row.value)}
                </Typography>
              </Box>
              <Box aria-hidden="true" sx={{ height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex' }}>
                {row.parts.map((part) => (
                  <Box key={part.key} sx={{ height: '100%', width: `${(part.value / max) * 100}%`, ...part.sx }} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function noCookNote(noCook) {
  const parts = [];
  if (noCook.notFound) parts.push(`новые блюда ${formatInt(noCook.notFound)}`);
  if (noCook.noPlace) parts.push(`цех не задан ${formatInt(noCook.noPlace)}`);
  return parts.join(' · ');
}

// model — buildKitchenModel (закрытые + открытые). nowLocal — местное время ресторана из живых данных
// («2026-10-22 19:30:00»), только для «Сегодня»; null — отметки «сейчас» нет
export default function KitchenChart({ model, selectedHour, onSelectHour, desktop, nowLocal = null }) {
  const [highlight, setHighlight] = useState(null); // цех, OPEN_KEY или NO_COOK_KEY
  const scrollRef = useRef(null);
  const columnRefs = useRef({});
  const fade = useScrollFade(scrollRef);
  // Легенда на телефоне — одна строка с горизонтальной прокруткой, затухание того же вида, что у графика
  const legendRef = useRef(null);
  const legendFade = useScrollFade(legendRef);

  const { hours, places, peak, openTotals, noCook } = model;
  const max = Math.max(1, ...hours.map((h) => h.items));

  const nowHour = nowLocal ? Number(nowLocal.slice(11, 13)) : null;
  const nowColumn = nowHour !== null && hours.some((h) => h.hour === nowHour) ? nowHour : null;
  // Текущего часа нет на графике (заказов ещё не было) — столбец не добавляем, но показываем, что данные свежие
  const nowNote = nowLocal && nowColumn === null ? `Сейчас ${nowLocal.slice(11, 16)} · новых заказов нет` : null;
  const defaultHour = nowColumn ?? peak?.hour ?? null;
  const activeHour = hours.some((h) => h.hour === selectedHour) ? selectedHour : defaultHour;
  const selected = hours.find((h) => h.hour === activeHour) || null;

  // На телефоне — показать выбранный час, если он за краем
  useEffect(() => {
    const node = columnRefs.current[activeHour];
    const container = scrollRef.current;
    if (!node || !container || container.scrollWidth <= container.clientWidth) return;
    container.scrollLeft = Math.max(0, node.offsetLeft - container.clientWidth / 2 + node.offsetWidth / 2);
  }, [activeHour, hours.length]);

  const toggleHighlight = (key) => setHighlight((prev) => (prev === key ? null : key));
  // Слой: цех и статус. Чип цеха — обе его части; «Открыто» — все заштрихованные; «Без цеха» — нейтральный блок
  const layerOpacity = (place, open) => {
    if (!highlight) return 1;
    if (highlight === OPEN_KEY) return open ? 1 : 0.2;
    return highlight === place ? 1 : 0.2;
  };

  const detailRows = selected
    ? [
        ...places
          .map((place) => {
            const closed = selected.closed[place.name] || 0;
            const open = selected.open[place.name] || 0;
            const color = placeColor(place);
            return {
              key: place.name,
              label: place.name,
              value: closed + open,
              note: open ? `${formatInt(open)} открыто` : null,
              dotSx: { backgroundColor: color },
              parts: [
                { key: 'closed', value: closed, sx: { backgroundColor: color } },
                { key: 'open', value: open, sx: placeHatchSx(color) },
              ],
            };
          })
          .filter((row) => row.value > 0),
        ...(selected.noCookTotal > 0
          ? [{
              key: NO_COOK_KEY,
              label: 'Без цеха',
              value: selected.noCookTotal,
              note: noCookNote(selected.noCook),
              dotSx: HATCH_SX,
              parts: [{ key: 'open', value: selected.noCookTotal, sx: HATCH_SX }],
            }]
          : []),
      ].sort((a, b) => b.value - a.value)
    : [];

  const extraChips = noCook.total > 0 || openTotals.items > 0;

  return (
    <Box component="section" aria-labelledby="kitchen-chart-title" sx={{ ...CARD_SURFACE_SX, width: '100%', boxSizing: 'border-box', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography id="kitchen-chart-title" component="h2" sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: 600, color: '#fff' }}>
          {desktop ? 'Позиции на кухню по часу открытия заказа' : 'Позиции по часам'}
        </Typography>
        {nowNote && <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>{nowNote}</Typography>}
      </Box>

      {/* Легенда: цеха (с открытыми), затем после разделителя — «Без цеха» и «Открыто».
          Десктоп — переносом по строкам; телефон — одна строка с прокруткой до краёв карточки */}
      <Box
        ref={legendRef}
        role="group"
        aria-label="Места приготовления"
        sx={
          desktop
            ? { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }
            : {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflowX: 'auto',
                mx: -2,
                px: 2,
                // Запас под рамку фокуса: overflow обрезал бы её сверху и снизу
                my: -0.5,
                py: 0.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                ...fadeMaskSx(legendFade),
              }
        }
      >
        {places.map((place) => (
          <LegendChip
            key={place.name}
            label={place.name}
            count={place.total}
            swatchSx={{ backgroundColor: placeColor(place) }}
            pressed={highlight === place.name}
            dimmed={Boolean(highlight) && highlight !== place.name}
            onClick={() => toggleHighlight(place.name)}
          />
        ))}
        {extraChips && <Box aria-hidden="true" sx={{ width: '1px', flexShrink: 0, alignSelf: 'stretch', minHeight: 24, mx: 0.5, backgroundColor: 'rgba(255,255,255,0.1)' }} />}
        {noCook.total > 0 && (
          <LegendChip
            label="Без цеха"
            count={noCook.total}
            swatchSx={HATCH_SX}
            pressed={highlight === NO_COOK_KEY}
            dimmed={Boolean(highlight) && highlight !== NO_COOK_KEY}
            onClick={() => toggleHighlight(NO_COOK_KEY)}
          />
        )}
        {openTotals.items > 0 && (
          <LegendChip
            label="Открыто"
            count={openTotals.items}
            swatchSx={HATCH_SX}
            pressed={highlight === OPEN_KEY}
            dimmed={Boolean(highlight) && highlight !== OPEN_KEY}
            onClick={() => toggleHighlight(OPEN_KEY)}
          />
        )}
      </Box>

      <Box>
        {/* Затухание края — маской самого блока: фон карточки градиентный, плашка поверх была бы видна */}
        <Box ref={scrollRef} sx={{ overflowX: 'auto', pb: 0.5, ...fadeMaskSx(fade) }}>
          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: `repeat(${hours.length}, minmax(${desktop ? 44 : MIN_COLUMN}px, 1fr))`,
              gap: { xs: '4px', md: '10px' },
              alignItems: 'end',
              minWidth: '100%',
            }}
          >
            {/* Сетка: 3 линии по высоте столбиков, без подписей; оси X без линии */}
            <Box aria-hidden="true" sx={{ position: 'absolute', left: 0, right: 0, top: BARS_TOP, height: BAR_HEIGHT, pointerEvents: 'none' }}>
              {GRID_LINES.map((share) => (
                <Box key={share} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${share * 100}%`, height: '1px', backgroundColor: 'rgba(255,255,255,0.05)' }} />
              ))}
            </Box>

            {hours.map((hour) => {
              const isSelected = hour.hour === activeHour;
              const isPeak = peak && hour.hour === peak.hour && hour.items > 0;
              const isNow = hour.hour === nowColumn;
              // Снизу вверх: по цехам — сплошная часть, над ней заштрихованная того же цеха; сверху «Без цеха»
              const layers = [];
              places.forEach((place) => {
                const color = placeColor(place);
                const closed = hour.closed[place.name] || 0;
                const open = hour.open[place.name] || 0;
                if (closed) layers.push({ key: `${place.name}|c`, place: place.name, open: false, value: closed, sx: solidSx(color) });
                if (open) layers.push({ key: `${place.name}|o`, place: place.name, open: true, value: open, sx: placeHatchSx(color) });
              });
              if (hour.noCookTotal) {
                layers.push({ key: NO_COOK_KEY, place: NO_COOK_KEY, open: true, value: hour.noCookTotal, sx: HATCH_SX });
              }
              const openText = hour.openItems ? `, из них ${itemsText(hour.openItems)} в открытых` : '';
              return (
                <ButtonBase
                  key={hour.hour}
                  ref={(node) => {
                    columnRefs.current[hour.hour] = node;
                  }}
                  onClick={() => onSelectHour(hour.hour)}
                  aria-pressed={isSelected}
                  aria-label={`${hour.label}: ${itemsText(hour.items)}${openText}, ${ordersText(hour.orders)}, ${formatMoney(hour.sum)}${isPeak ? ', пик' : ''}${isNow ? ', сейчас' : ''}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: `${ROW_GAP}px`,
                    borderRadius: '6px',
                    '&:hover .kitchen-bar': { opacity: isSelected ? 1 : 0.7 },
                    '&.Mui-focusVisible': { outline: `2px solid ${ACCENT}`, outlineOffset: '2px' },
                  }}
                >
                  <Box sx={{ height: PEAK_ROW, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {isPeak && (
                      <Box component="span" sx={{ px: 0.75, height: 18, lineHeight: '18px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, color: '#e4e4e7', backgroundColor: 'rgba(255,255,255,0.08)' }}>
                        Пик
                      </Box>
                    )}
                  </Box>
                  <Typography
                    className="kitchen-bar"
                    sx={{ ...fadeSx, height: COUNT_ROW, lineHeight: `${COUNT_ROW}px`, textAlign: 'center', fontSize: '14px', fontWeight: 700, color: '#e4e4e7', fontVariantNumeric: 'tabular-nums', opacity: isSelected ? 1 : 0.35 }}
                  >
                    {formatInt(hour.items)}
                  </Typography>
                  <Box sx={{ position: 'relative', height: BAR_HEIGHT }}>
                    {isNow && (
                      <>
                        <Box aria-hidden="true" sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', borderLeft: `1px dashed ${NOW_LINE}`, pointerEvents: 'none' }} />
                        <Box aria-hidden="true" sx={{ position: 'absolute', bottom: -4, left: '50%', width: 8, height: 8, ml: '-4px', borderRadius: '50%', backgroundColor: NOW_COLOR, zIndex: 1 }} />
                      </>
                    )}
                    <Box
                      className="kitchen-bar"
                      sx={{ ...fadeSx, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column-reverse', gap: '2px', opacity: isSelected ? 1 : 0.35 }}
                    >
                      {/* Первый слой — у основания; скруглены только верхние углы верхнего слоя.
                          Щели между слоями вычитаются из высоты, иначе у столбика с многими слоями
                          (пик) они складываются и столбик вылезает на число над ним */}
                      {layers.map((layer, index) => (
                        <Box
                          key={layer.key}
                          sx={{
                            ...layerSx,
                            ...layer.sx,
                            flexShrink: 1,
                            minHeight: '2px',
                            height: `calc((100% - ${(layers.length - 1) * 2}px) * ${layer.value / max})`,
                            borderRadius: index === layers.length - 1 ? '6px 6px 0 0' : 0,
                            opacity: layerOpacity(layer.place, layer.open),
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Typography sx={{ textAlign: 'center', fontSize: { xs: '11px', md: '12px' }, color: isSelected ? '#fff' : MUTED_TEXT_COLOR, fontWeight: isSelected ? 700 : 400 }}>
                    {desktop ? hour.label : String(hour.hour)}
                  </Typography>
                  {/* Строка «сейчас» — только если отметка есть на графике, иначе пустой отступ под часами */}
                  {nowColumn !== null && (
                    <Typography sx={{ height: 14, lineHeight: '14px', textAlign: 'center', fontSize: '11px', color: NOW_TEXT }}>
                      {isNow ? 'сейчас' : ''}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      </Box>

      <HourDetails hour={selected} rows={detailRows} />
    </Box>
  );
}
